import fastdom from 'fastdom';

import addClass from 'utils/add-class';
import transitionEnd from 'utils/transition-end';

export type Config = {
  classes?: string | string[];
  index?: number | null;
};

export type Instance = {
  show: (options?: Config) => void;
  hide: () => void;
  toggle: (options?: Config) => void;
};

class Overlay {
  private readonly element: HTMLDivElement;

  private readonly classes: { base: string; active: string };

  private isActive: boolean;

  private readonly openedBy: Set<object> = new Set();

  constructor() {
    this.classes = {
      base: 'overlay',
      active: 'overlay--active',
    };
    this.isActive = false;

    this.element = document.createElement('div');
    this.element.hidden = true;
    this.element.classList.add(this.classes.base);

    document.body.appendChild(this.element);
  }

  toggle(opener: object, options?: Config): void {
    if (this.openedBy.has(opener)) {
      this.hide(opener);
    } else {
      this.show(opener, options);
    }
  }

  show(opener: object, options?: Config): void {
    this.openedBy.add(opener);

    if (this.isActive) {
      return;
    }

    this.isActive = true;
    this.element.hidden = false;

    if (options) {
      if (options.classes) {
        addClass(this.element, options.classes);
      }

      if (options.index) {
        this.element.style.zIndex = options.index.toString();
      }
    }

    fastdom.mutate(() => {
      this.element.classList.add(this.classes.active);
    });
  }

  hide(opener: object): void {
    this.openedBy.delete(opener);

    if (this.openedBy.size > 0 || !this.isActive) {
      return;
    }

    this.isActive = false;

    fastdom.mutate(() => {
      this.element.className = this.classes.base;

      transitionEnd(this.element, () => {
        if (!this.isActive) {
          this.element.hidden = true;
          this.element.style.zIndex = '';
        }
      });
    });
  }
}

const overlay = new Overlay();

export const createOverlay = (opener: object): Instance => ({
  show: (options?: Config) => overlay.show(opener, options),
  hide: () => overlay.hide(opener),
  toggle: (options?: Config) => overlay.toggle(opener, options),
});
